import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Modal from "./Modal.tsx";
import StyledButton from "./Button.tsx";
import {
  ModalInput,
  ModalLabel,
  ModalButtonRow,
  CancelButton,
  ErrorMessage,
} from "./ModalFormComponents.tsx";
import { uploadCategoryDocument } from "../services/categories.service.ts";
import { parseApiError } from "../utils/errorHandler.ts";

interface AddDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number;
  categoryType: string;
  nodeId: number;
  onSuccess?: () => void;
}

const FileName = styled.div`
  font-weight: 600;
  color: #001f52;
  margin-bottom: 0.75rem;
  word-break: break-word;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

export default function AddDocumentModal({
  isOpen,
  onClose,
  categoryId,
  categoryType,
  nodeId,
  onSuccess,
}: AddDocumentModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setFile(null);
    setName("");
    setDescription("");
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) {
      return;
    }

    if (!selected.name.toLowerCase().endsWith(".pdf")) {
      setError(`${selected.name} er ikke en PDF fil. Kun PDF filer er tilladt.`);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setError(null);
    setFile(selected);
    setName(selected.name.replace(/\.pdf$/i, ""));
    setDescription("");
  };

  const handleSubmit = async () => {
    setError(null);

    if (!file) {
      setError("Vælg en PDF-fil");
      return;
    }

    const docName = name.trim() || file.name.replace(/\.pdf$/i, "");
    if (!docName) {
      setError("Navn er påkrævet");
      return;
    }

    setUploading(true);
    try {
      await uploadCategoryDocument(
        categoryId,
        categoryType,
        nodeId,
        file,
        docName,
        description.trim() || undefined
      );
      handleClose();
      onSuccess?.();
    } catch (err: unknown) {
      const errMessage = err instanceof Error ? err.message : "";
      const msg = /standard/i.test(errMessage)
        ? "Kan ikke tilføje dokumenter til standardkategorier"
        : errMessage.includes("PDF")
          ? errMessage
          : parseApiError(err);
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Tilføj dokument">
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {!file && (
        <div style={{ marginTop: "1rem" }}>
          <StyledButton
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Vælg PDF-fil
          </StyledButton>
        </div>
      )}

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        onChange={handleFileChange}
      />

      {file && (
        <>
          <FileName>{file.name}</FileName>

          <ModalLabel>Navn *</ModalLabel>
          <ModalInput
            placeholder="Indtast navn..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={uploading}
          />

          <ModalLabel>Beskrivelse</ModalLabel>
          <ModalInput
            placeholder="Indtast beskrivelse (valgfrit)..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={uploading}
          />
        </>
      )}

      <ModalButtonRow>
        <CancelButton onClick={handleClose} disabled={uploading}>
          Annuller
        </CancelButton>
        <StyledButton onClick={handleSubmit} disabled={uploading || !file}>
          {uploading ? "Tilføjer..." : "Tilføj"}
        </StyledButton>
      </ModalButtonRow>
    </Modal>
  );
}
